import {
  CalendarClock,
  Check,
  ClipboardList,
  FileText,
  HeartPulse,
  Link2,
  Mail,
  Pill,
  Stethoscope,
} from 'lucide-react'
import { memo, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import type { TimelineEntry } from '@snootsvet/shared'
import {
  type DateFormat,
  getPrimaryOrder,
  type PatientWithOwners,
  resolveDayGroup,
} from '@snootsvet/shared'

import { recordFrame, useViewerFormat } from '@/frontend/components/formatted-datetime'
import { nowDividerIndex, TimelineNowDivider } from '@/frontend/components/timeline-now-divider'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { CommunicationEntry } from '@/frontend/features/communications/communication-entry'
import { useActiveConsultation } from '@/frontend/features/consultations/active-consultation-context'
import { ConsultationWorkspace } from '@/frontend/features/consultations/consultation-workspace'
import {
  PatientTimelineFilters,
  usePatientTimelineFilters,
} from '@/frontend/features/patient/patient-timeline-filters'
import { useGetPatientTimeline } from '@/frontend/hooks/api/patients/use-get-patient-timeline'
import { useCopyToClipboard } from '@/frontend/hooks/use-copy-to-clipboard'
import { cn } from '@/frontend/lib/utils'

import { AppointmentEntry } from './timeline/appointment-entry'
import { ConsultationEntry } from './timeline/consultation-entry'
import { NoteEntry } from './timeline/note-entry'
import { TaskEntry } from './timeline/task-entry'
import { TreatmentEntry } from './timeline/treatment-entry'
import { VitalsEntry } from './timeline/vitals-entry'

// ---------------------------------------------------------------------------
// Timeline utils
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Timeline icon — rendered in the left connector
// ---------------------------------------------------------------------------

/** Returns the lucide glyph (with its type-specific color) for a timeline entry type. */
function typeGlyph(type: TimelineEntry['type']) {
  switch (type) {
    case 'consultation':
      return <Stethoscope className="size-3 text-consultation" />
    case 'note':
      return <FileText className="size-3 text-note" />
    case 'vitals':
      return <HeartPulse className="size-3 text-vitals" />
    case 'appointment':
      return <CalendarClock className="size-3 text-info-fg" />
    case 'treatment':
      return <Pill className="size-3 text-treatment" />
    case 'task':
      return <ClipboardList className="size-3 text-task" />
    case 'communication':
      return <Mail className="size-3.5 text-communication" />
  }
}

/**
 * Timeline connector icon that doubles as a "copy link to this entry" button.
 * At rest it shows the entry's type glyph; on hover it swaps to a link icon,
 * and clicking copies a deep-link (current URL + `#<entryId>`) to the clipboard.
 * The hash triggers the existing scroll-into-view + highlight behaviour on load.
 */
function TimelineIconButton({
  type,
  id,
  isFuture,
}: {
  type: TimelineEntry['type']
  id: string
  isFuture: boolean
}) {
  const { copied, copy } = useCopyToClipboard()

  function copyLink() {
    const { origin, pathname, search } = window.location
    copy(`${origin}${pathname}${search}#${id}`, 'Link')
  }

  return (
    <button
      type="button"
      onClick={copyLink}
      title="Copy link"
      aria-label="Copy link to this entry"
      className={cn(
        'group flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border bg-card ring-3 ring-background transition-colors hover:border-primary',
        isFuture && 'border-dashed'
      )}
    >
      {copied ? (
        <Check className="size-3 text-success-fg" />
      ) : (
        <>
          <span className="group-hover:hidden">{typeGlyph(type)}</span>
          <Link2 className="hidden size-3 text-primary group-hover:block" />
        </>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Entry renderer — dispatches to the correct component by type
// ---------------------------------------------------------------------------

interface InnerTimelineEntryItemProps {
  entry: TimelineEntry
  patient: PatientWithOwners
  isActive: boolean
  isFuture: boolean
}

function InnerTimelineEntryItem({
  entry,
  patient,
  isActive,
  isFuture,
}: InnerTimelineEntryItemProps) {
  switch (entry.type) {
    case 'consultation':
      if (isActive) {
        return <ConsultationWorkspace consultationId={entry.consultation.id} />
      }
      return <ConsultationEntry consultation={entry.consultation} isFuture={isFuture} />
    case 'note':
      return <NoteEntry note={entry.note} />
    case 'vitals':
      return <VitalsEntry vitals={entry.vitals} />
    case 'appointment':
      return <AppointmentEntry appointment={entry.appointment} isFuture={isFuture} />
    case 'treatment':
      return <TreatmentEntry treatment={entry.treatment} patient={patient} />
    case 'task':
      return <TaskEntry task={entry.task} />
    case 'communication':
      return <CommunicationEntry communication={entry.communication} />
  }
}

// ---------------------------------------------------------------------------
// Entry — wraps each timeline item with scroll-into-view, hash highlight,
// and active consultation handling
// ---------------------------------------------------------------------------

interface TimelineEntryItemProps {
  entry: TimelineEntry
  /** The timeline's patient — entries that render forms need it (e.g. species). */
  patient: PatientWithOwners
  highlighted: boolean
  isFuture: boolean
}

const TimelineEntryItem = memo(function TimelineEntryItem({
  entry,
  patient,
  highlighted,
  isFuture,
}: TimelineEntryItemProps) {
  const { consultationId } = useActiveConsultation()
  const ref = useRef<HTMLDivElement>(null)
  const isActive = entry.type === 'consultation' && entry.consultation.id === consultationId

  useEffect(() => {
    if (highlighted && !isActive) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [highlighted, isActive])

  return (
    <div
      ref={ref}
      className={cn(
        'relative',
        isActive ? '-ml-10' : 'rounded-xl',
        !isActive && highlighted && 'animate-timeline-highlight'
      )}
    >
      {!isActive && (
        <div className="absolute -left-9 top-3">
          <TimelineIconButton type={entry.type} id={entryId(entry)} isFuture={isFuture} />
        </div>
      )}
      <InnerTimelineEntryItem
        entry={entry}
        patient={patient}
        isActive={isActive}
        isFuture={isFuture}
      />
    </div>
  )
})

// ---------------------------------------------------------------------------
// Date grouping helpers
// ---------------------------------------------------------------------------

/**
 * The clinic an entry happened at, or null when it has none. A treatment's is its
 * dispensing order's — the same one `TreatmentEntry` badges and stamps — so the
 * header and the entry read the same clinic.
 *
 * Spelled out rather than `default: return null` so a future entry type that
 * *does* carry a clinic fails to compile here instead of silently resolving in
 * the viewer's frame.
 */
function entryClinicId(entry: TimelineEntry): string | null {
  switch (entry.type) {
    case 'appointment':
      return entry.appointment.clinicId
    case 'consultation':
      return entry.consultation.clinicId
    case 'treatment':
      return getPrimaryOrder(entry.treatment.orders)?.clinicId ?? null
    case 'note':
    case 'vitals':
    case 'task':
    case 'communication':
      return null
  }
}

/**
 * Groups timeline entries by date label, preserving order.
 *
 * `resolveDayGroup` returns the label and whether the day is still ahead from one
 * comparison, so a group headed "Tomorrow" is always flagged upcoming. Each entry
 * resolves its day in the frame it renders in — its clinic's zone, or the viewer's
 * when it has none — so a header can never name a different day than the entry
 * beneath it.
 */
function groupByDate(
  entries: TimelineEntry[],
  viewerFmt: DateFormat
): { label: string; isFuture: boolean; entries: TimelineEntry[] }[] {
  const groups: { label: string; isFuture: boolean; entries: TimelineEntry[] }[] = []

  for (const entry of entries) {
    const { label, isFuture } = resolveDayGroup(
      entry.date,
      recordFrame(entryClinicId(entry), viewerFmt)
    )
    const last = groups[groups.length - 1]
    if (last?.label === label) {
      last.entries.push(entry)
    } else {
      groups.push({ label, isFuture, entries: [entry] })
    }
  }

  return groups
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TimelineSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface PatientTimelineProps {
  /** The timeline's patient. Passed down so entries don't each re-read it from cache. */
  patient: PatientWithOwners
}

/** Patient timeline — unified feed of consultations, notes, vitals, and appointments. */
export function PatientTimeline({ patient }: PatientTimelineProps) {
  const patientId = patient.id
  // Headers resolve days in the viewer's own frame, matching the entries beneath.
  const viewerFmt = useViewerFormat()
  const { hash } = useLocation()
  const highlightId = hash.slice(1) || null
  const { filters, setFilters } = usePatientTimelineFilters()
  const { data: entries, isLoading } = useGetPatientTimeline(patientId, filters)

  // The server already filters; this drops a just-deselected type from the
  // previous feed while the new one loads.
  const filtered = (entries ?? []).filter((entry) => filters.includes(entry.type))

  const groups = groupByDate(filtered, viewerFmt)
  const dividerIndex = nowDividerIndex(groups)

  return (
    <div className="space-y-3">
      <PatientTimelineFilters filters={filters} onFiltersChange={setFilters} />

      {/* Content */}
      {isLoading ? (
        <TimelineSkeleton />
      ) : groups.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          {filters.length === 0 ? 'No timeline types selected.' : 'No timeline entries.'}
        </p>
      ) : (
        <div className="space-y-5">
          {groups.map((group, i) => (
            <div key={group.label}>
              {i === dividerIndex && <TimelineNowDivider />}

              {/* Date group header — its rule goes dashed too, so the whole day
                  above the boundary reads as provisional. */}
              <div className="mb-3 flex items-center gap-3">
                <span className="shrink-0 text-sm font-medium text-muted-foreground">
                  {group.label}
                </span>
                <div
                  className={cn(
                    'flex-1',
                    group.isFuture ? 'border-t border-dashed border-border' : 'h-px bg-border'
                  )}
                />
              </div>

              {/* Entries with left connector line */}
              <div className="relative space-y-3 pl-9">
                {/* Vertical connector — dashed while the entries above the boundary
                    haven't happened yet. */}
                <div
                  className={cn(
                    'absolute top-0 bottom-0 left-3 -translate-x-1/2',
                    group.isFuture ? 'border-l border-dashed border-border' : 'w-px bg-border'
                  )}
                />

                {group.entries.map((entry) => {
                  const id = entryId(entry)
                  return (
                    <TimelineEntryItem
                      key={id}
                      entry={entry}
                      patient={patient}
                      highlighted={id === highlightId}
                      isFuture={group.isFuture}
                    />
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Extracts a unique ID from a timeline entry for React keys. */
function entryId(entry: TimelineEntry): string {
  switch (entry.type) {
    case 'consultation':
      return entry.consultation.id
    case 'note':
      return entry.note.id
    case 'vitals':
      return entry.vitals.id
    case 'appointment':
      return entry.appointment.id
    case 'treatment':
      return entry.treatment.id
    case 'task':
      return entry.task.id
    case 'communication':
      return entry.communication.id
  }
}
